import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  keepContent,
  loadContent,
  loadMoreContent,
  loadMoreFromRootChannelFailure,
  loadMoreFromRootChannelSuccess,
  loadMoreFromSubscribedChannelFailure,
  loadMoreFromSubscribedChannelSuccess,
  loadMoreFromUserHistoryChannelSuccess,
  loadRootChannelFailure,
  loadRootChannelSuccess,
  loadSubscribedChannelFailure,
  loadSubscribedChannelSuccess,
  loadUserHistoryChannelFailure,
  loadUserHistoryChannelSuccess,
  setSource,
} from './newsfeed-results.actions';
import { NewsfeedContentSource, NewsfeedResult } from './newsfeed-results.models';

export const NEWSFEED_RESULTS_FEATURE_KEY = 'newsfeedResults';

export interface NewsfeedResultsState extends EntityState<NewsfeedResult> {
  source: NewsfeedContentSource;
  loaded: boolean;
  loadingMore?: boolean;
  error?: string | null;
  totalAmount?: number;
  nextCursor?: string;
  seed?: number;
}

export interface NewsfeedResultsPartialState {
  readonly [NEWSFEED_RESULTS_FEATURE_KEY]: NewsfeedResultsState;
}

export function selectNewsfeedResultId(newsfeedResultItem: NewsfeedResult) {
  return `${newsfeedResultItem.type}/${newsfeedResultItem.id}`;
}

export const newsfeedResultsAdapter: EntityAdapter<NewsfeedResult> = createEntityAdapter<NewsfeedResult>({
  selectId: selectNewsfeedResultId,
});

export const initialNewsfeedResultsState: NewsfeedResultsState = newsfeedResultsAdapter.getInitialState({
  source: { feed: 'ROOT' },
  loaded: false,
});

const reducer = createReducer(
  initialNewsfeedResultsState,
  // Basic actions
  on(setSource, (state, { source }) => ({ ...state, source, loaded: false, error: null })),
  on(loadContent, (state) =>
    newsfeedResultsAdapter.setAll([], {
      ...state,
      loaded: false,
      loadingMore: false,
      totalAmount: undefined,
      seed: undefined,
      nextCursor: undefined,
    })
  ),
  on(loadMoreContent, (state) => ({ ...state, loadingMore: true })),
  on(keepContent, (state) => ({ ...state, loaded: true })),
  // Load content
  on(loadRootChannelSuccess, (state, { results, count, seed }) =>
    isRootNewsfeed(state)
      ? newsfeedResultsAdapter.setAll(results, { ...state, loaded: true, totalAmount: count, seed })
      : state
  ),
  on(loadSubscribedChannelSuccess, (state, { results, fairId, exhibitorId, nextCursor }) =>
    isSameSubscriptionNewsfeed(state, fairId, exhibitorId)
      ? newsfeedResultsAdapter.setAll(results, { ...state, loaded: true, nextCursor })
      : state
  ),
  on(loadUserHistoryChannelSuccess, (state, { results, count }) =>
    isUserHistoryNewsfeed(state)
      ? newsfeedResultsAdapter.setAll(results, { ...state, loaded: true, totalAmount: count })
      : state
  ),
  // Load more content
  on(loadMoreFromRootChannelSuccess, (state, { results }) =>
    isRootNewsfeed(state) ? newsfeedResultsAdapter.addMany(results, { ...state, loadingMore: false }) : state
  ),
  on(loadMoreFromSubscribedChannelSuccess, (state, { results, fairId, exhibitorId, nextCursor }) =>
    isSameSubscriptionNewsfeed(state, fairId, exhibitorId)
      ? newsfeedResultsAdapter.addMany(results, { ...state, nextCursor, loadingMore: false })
      : state
  ),
  on(loadMoreFromUserHistoryChannelSuccess, (state, { results }) =>
    isUserHistoryNewsfeed(state) ? newsfeedResultsAdapter.addMany(results, { ...state, loadingMore: false }) : state
  ),
  // Errors
  on(
    loadRootChannelFailure,
    loadSubscribedChannelFailure,
    loadUserHistoryChannelFailure,
    loadMoreFromRootChannelFailure,
    loadMoreFromSubscribedChannelFailure,
    (state, { error }) => newsfeedResultsAdapter.setAll([], { ...state, loaded: true, loadingMore: false, error })
  )
);

export function newsfeedResultsReducer(state: NewsfeedResultsState | undefined, action: Action) {
  return reducer(state, action);
}

function isRootNewsfeed(state: NewsfeedResultsState): boolean {
  return state.source.feed === 'ROOT';
}

function isUserHistoryNewsfeed(state: NewsfeedResultsState): boolean {
  return state.source.feed === 'HISTORY';
}

function isSameSubscriptionNewsfeed(state: NewsfeedResultsState, fairId: string, exhibitorId: string): boolean {
  return state.source.feed === 'SUBSCRIPTION' && state.source.fair === fairId && state.source.channel === exhibitorId;
}
